import {
  PackagingMedicationData,
  PackagingDrugVerification,
  MixtureIngredientScan,
  getEmptyPackagingVerification,
  isPackagingVerificationComplete,
  type LabelSplit,
  type LabelSplitOverride,
} from '../types/packagingVerification.types';
import type {
  TabletQuantityState,
  TabletBottleScan,
  DosageForm,
} from '../types/packagingOptimized.types';

/**
 * Normalize a UPC code by stripping leading zeros for comparison.
 * Kroll stores UPCs with leading zeros (e.g., "0000012345") but barcode
 * scanners return them without (e.g., "12345"). This ensures both values
 * match regardless of leading zero padding.
 */
export const normalizeUpc = (upc: string): string => {
  if (!upc) return '';
  return upc.replace(/^0+/, '') || '0';
};

/**
 * Build the set of comparable forms for a UPC value.
 *
 * Kroll sometimes stores the full GS1 element string — AI "01" followed by a
 * 14-digit GTIN (e.g. "0105706020001007" for Monoferric) — instead of a bare
 * UPC/GTIN. Scanners and manual entry can produce either the full 16-digit
 * string, the bare GTIN, or the value with leading zeros trimmed, so every
 * value is compared both as-is and (when it has the AI "01" shape) with the
 * AI prefix stripped.
 */
const upcCandidates = (upc: string): string[] => {
  const trimmed = (upc || '').trim();
  const candidates = [normalizeUpc(trimmed)];
  if (/^01\d{14}$/.test(trimmed)) {
    candidates.push(normalizeUpc(trimmed.slice(2)));
  }
  return candidates;
};

/**
 * Compare two UPC codes, ignoring leading zeros and an optional GS1 AI "01"
 * prefix on either value. Returns true if any normalized forms match.
 */
export const upcMatch = (upc1: string, upc2: string): boolean => {
  const candidates2 = upcCandidates(upc2);
  return upcCandidates(upc1).some((c) => candidates2.includes(c));
};

/**
 * Extracts medication data from order object for packaging verification
 * Handles both multi-drug orders (with medications array) and single-drug orders
 *
 * UPC Resolution Priority:
 * 1. billingData.medications[].upc (from order creation)
 * 2. packagingData.scans[].barcode (from previous scan session)
 */
export const extractMedicationsForPackaging = (order: any): PackagingMedicationData[] => {
  // Check if we have medications array in billingData
  const allMedications = order?.metadata?.billingData?.medications;
  const packagingData = order?.metadata?.packagingData;

  // Filter: exclude medications split to troubleshooting
  const splitRxNumbers = new Set(
    (order?.metadata?.troubleshootingSplitHistory || []).map((h: any) => String(h.rxNumber))
  );
  const medications = allMedications?.filter((med: any) => {
    const rx = String(med.rxNumber);
    if (splitRxNumbers.has(rx)) return false;
    return true;
  });

  if (medications && Array.isArray(medications) && medications.length > 0) {
    // Multi-drug order - extract from medications array
    return medications.map((med: any) => {
      // Try billingData UPC first, then packagingData barcode as fallback
      let upc = med.upc || '';

      if (!upc && packagingData && Array.isArray(packagingData)) {
        // Find matching packagingData by rxNumber or DIN
        const savedData = packagingData.find((p: any) =>
          p.rxNumber === med.rxNumber || p.rxNumber === String(med.rxNumber)
        );
        if (savedData?.scans?.[0]?.barcode) {
          // Use barcode from first scan
          upc = savedData.scans[0].barcode;
        }
      }

      return {
        rxNumber: med.rxNumber || order.rxNumber || '',
        medicationName: med.medicationName || 'Unknown Medication',
        din: med.din || '',
        drugId: med.drugId || '',
        upc, // Now with packagingData fallback
        strength: med.strength || '',
        quantity: med.quantity || 0,
        packCount: med.packCount || 1,
        packSize: med.packSize != null ? Number(med.packSize) : undefined, // GP-475: per-pack size from Kroll (surfaced in the Review strip)
        daysSupply: med.daysSupply != null ? Number(med.daysSupply) : undefined, // GP-891
        labels: med.labels != null ? Number(med.labels) : undefined,
        status: med.status, // Rx.Status from Kroll
        rxAdjState: med.rxAdjState ?? null, // Kroll Rx.AdjState (non-clean = stuck adjudication flag)
        // Drug card fields from Kroll resync
        location: med.location || undefined,
        onHandQty: med.onHandQty != null ? Number(med.onHandQty) : undefined,
        drugNotes: med.drugDescription || undefined,
        drugComment: med.drugComments || med.drugComment || undefined,
        // PRX-59: the prescription-status verdict. This rebuild is a field
        // whitelist, so it must be listed here or packaging gates nothing.
        pmsGate: med.pmsGate ?? null,
        // PRX-18: the per-drug label split rule (Fillware). Same whitelist rule.
        labelSplit: med.labelSplit ?? null,
      };
    });
  }

  // Single-drug order - extract from top-level order data (backward compatibility)
  // Also check packagingData for UPC fallback
  let singleUpc = order?.metadata?.billingData?.upc || '';

  if (!singleUpc && packagingData) {
    const savedData = Array.isArray(packagingData) ? packagingData[0] : packagingData;
    if (savedData?.scans?.[0]?.barcode) {
      singleUpc = savedData.scans[0].barcode;
    }
  }

  return [
    {
      rxNumber: order?.rxNumber || '',
      medicationName: order?.medicationName || order?.drugName || 'Unknown Medication',
      din: order?.metadata?.din || '',
      drugId: order?.metadata?.drugId || '',
      upc: singleUpc,
      strength: order?.metadata?.billingData?.strength || order?.strength || '',
      quantity: order?.quantity || 0,
      packCount: order?.packCount || order?.metadata?.packCount || 1,
      packSize: order?.metadata?.billingData?.packSize != null // GP-475
        ? Number(order.metadata.billingData.packSize)
        : undefined,
      daysSupply: order?.metadata?.billingData?.daysSupply != null
        ? Number(order.metadata.billingData.daysSupply)
        : undefined, // GP-891
    },
  ];
};

/**
 * GP-891: Patient-safety gate for Packaging.
 *
 * A dispensed container must not expire before the patient finishes the portion
 * of the supply it covers. The rule is FIFO pro-rated: the patient uses the
 * earliest-expiring container first, so each one only has to last for its share
 * of the days supply. Sorting containers by expiry ascending, container `i`
 * (holding cumulative quantity `cumQty_i` of `totalQty`) must satisfy:
 *
 *   expiry_i  >=  today + ceil( (cumQty_i / totalQty) * daysSupply )  days
 *
 * The last (latest-expiring) container therefore must cover the full days
 * supply, and a single-container fill collapses to `expiry >= today + daysSupply`.
 *
 * Returns the first container that violates the rule (in FIFO order), or `null`
 * if every container passes. The check is SKIPPED (returns `null`) when
 * `daysSupply` is not a positive number — we cannot compute a requirement
 * without it, and per the project's no-silent-fallback rule we never substitute
 * a default that would weaken or falsely trigger the gate; callers should log
 * the skip. Containers whose expiry can't be parsed are treated as violations
 * (fail closed) so malformed data can't slip an expiring product through.
 *
 * Dates are built with local Date constructors (never string parsing or UTC),
 * per the project date-handling rules — each pharmacy runs in one timezone.
 */
export interface ExpiryContainer {
  /** Expiry in MM/YYYY or DD/MM/YYYY (the packaging UI's format). */
  expiryDate: string;
  /** Quantity dispensed from this container. */
  quantity: number;
}

export interface ExpiryDaysSupplyViolation {
  /** Position of the offending container in FIFO (expiry-ascending) order. */
  fifoIndex: number;
  expiryDate: string;
  /** YYYY-MM-DD the container must remain valid through. */
  requiredThroughDate: string;
  /** Days of supply this container must cover (pro-rated, rounded up). */
  requiredDays: number;
  /** Cumulative quantity dispensed through this container. */
  coveredQty: number;
  totalQty: number;
}

/**
 * Parse an MM/YYYY or DD/MM/YYYY expiry to the LAST local date the product is
 * still usable. MM/YYYY (no day) means usable through the end of that month.
 * Returns null if the string isn't a recognized expiry shape.
 */
const parseExpiryToLastValidDate = (expiry: string): Date | null => {
  if (!expiry) return null;
  const parts = expiry.split('/');
  if (parts.length === 2) {
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);
    if (!Number.isFinite(month) || !Number.isFinite(year) || month < 1 || month > 12) return null;
    // Day 0 of the next month = last day of this month (local midnight).
    return new Date(year, month, 0);
  }
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (![day, month, year].every(Number.isFinite) || month < 1 || month > 12 || day < 1 || day > 31) return null;
    return new Date(year, month - 1, day);
  }
  return null;
};

/** Format a Date to YYYY-MM-DD using local getters (no UTC / toISOString). */
const toLocalYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const findExpiryDaysSupplyViolation = (
  containers: ExpiryContainer[],
  totalQty: number,
  daysSupply: number,
  today: Date = new Date(),
): ExpiryDaysSupplyViolation | null => {
  if (!(daysSupply > 0) || containers.length === 0) return null;

  // Normalize "today" to local midnight so day arithmetic is stable.
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Pro-rate only when every container carries a positive quantity and they sum
  // to a positive total; otherwise fall back to requiring each container to
  // cover the FULL days supply (stricter — safe when quantities are unknown).
  const canProrate = totalQty > 0 && containers.every((c) => (c.quantity || 0) > 0);

  const sorted = [...containers].sort((a, b) => {
    const da = parseExpiryToLastValidDate(a.expiryDate);
    const db = parseExpiryToLastValidDate(b.expiryDate);
    // Unparseable expiries sort first so they're evaluated (and fail) early.
    const ta = da ? da.getTime() : -Infinity;
    const tb = db ? db.getTime() : -Infinity;
    return ta - tb;
  });

  let cumulativeQty = 0;
  for (let i = 0; i < sorted.length; i++) {
    const container = sorted[i];
    cumulativeQty += container.quantity || 0;
    const fraction = canProrate ? cumulativeQty / totalQty : 1;
    const requiredDays = Math.ceil(fraction * daysSupply);
    const requiredThrough = new Date(base.getFullYear(), base.getMonth(), base.getDate() + requiredDays);
    const lastValid = parseExpiryToLastValidDate(container.expiryDate);

    if (!lastValid || lastValid.getTime() < requiredThrough.getTime()) {
      return {
        fifoIndex: i,
        expiryDate: container.expiryDate,
        requiredThroughDate: toLocalYmd(requiredThrough),
        requiredDays,
        coveredQty: cumulativeQty,
        totalQty,
      };
    }
  }
  return null;
};

/**
 * Convert ISO date to display format: DD/MM/YYYY or MM/YYYY
 * YYYY-MM-DD → DD/MM/YYYY (preserves day)
 * YYYY-MM → MM/YYYY (no day)
 */
export const formatISOToMMYYYY = (isoDate: string): string => {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  if (parts.length === 2) {
    const [year, month] = parts;
    return `${month}/${year}`;
  }
  return isoDate;
};

/**
 * Initializes packaging verification objects for each medication
 * Populates with saved data if available
 */
export const initializePackagingVerifications = (
  medications: PackagingMedicationData[],
  hasNarcotics: boolean = false,
  packagingData?: any
): PackagingDrugVerification[] => {
  return medications.map((med) => {
    const empty = getEmptyPackagingVerification(med, hasNarcotics);
    
    // Try to find matching saved data
    let savedData = null;
    if (packagingData) {
      if (Array.isArray(packagingData)) {
        savedData = packagingData.find((p: any) => p.rxNumber === med.rxNumber || p.rxNumber === String(med.rxNumber));
      } else {
        // Legacy single object
        savedData = packagingData;
      }
    }

    if (savedData) {
      return {
        ...empty,
        lotNumber: savedData.lotNumber || '',
        expiryDate: savedData.expiryDate ? formatISOToMMYYYY(savedData.expiryDate) : '',
        scans: savedData.scans || [],
        isFullyVerified: true, // If data exists, it was verified
        completedSteps: [0, 1, 2, 3], // Mark all steps complete
        labelsGenerated: savedData.labelPrinted || false,
        technicianNotes: savedData.notes || '',
        packagingType: savedData.packageType || 'vial',
        blueBagNumber: savedData.blueBagNumber || '',
        childProofCap: true, // Default to true if saved
        // Restore other flags if they were saved, otherwise defaults
      };
    }

    return empty;
  });
};

/**
 * GP-1322: the verification patch applied when every ingredient of a mixture
 * has been confirmed.
 *
 * A compound has no scannable product barcode, so the tech types each
 * ingredient's lot/expiry and packaging builds ONE synthetic scan to stand in
 * for the drug-product scan. The patch must carry `lotNumber`/`expiryDate` as
 * well as the scan: `buildPackagingItem` persists `verification.lotNumber` /
 * `verification.expiryDate` into `metadata.packagingData[]`, the print gate
 * reads `currentVerification.expiryDate`, and the label modal prop reads it too.
 * Omitting them (the original bug) meant a typed expiry never reached the
 * printed label or the saved record — it lived only in the ingredient rows.
 * Mirrors what the pack/bottle path does with its first confirmed scan.
 */
export const buildMixtureVerificationUpdate = (
  ingredientScans: Array<Pick<MixtureIngredientScan, 'lotNumber' | 'expiryDate'>>,
  rxNumber?: string | number,
): Partial<PackagingDrugVerification> => {
  const first = ingredientScans[0];
  const syntheticScan = {
    barcode: `MIXTURE-${rxNumber || 'unknown'}`,
    lotNumber: first?.lotNumber || '',
    expiryDate: first?.expiryDate || '',
    timestamp: new Date().toISOString(),
    confirmed: true,
  };

  return {
    scans: [syntheticScan],
    drugProductScanned: true,
    // The values the technician typed — without these the expiry never reaches
    // the saved packaging record, the print gate, or the label.
    lotNumber: syntheticScan.lotNumber,
    expiryDate: syntheticScan.expiryDate,
    isFullyVerified: true,
    verifiedAt: new Date().toISOString(),
  };
};

/**
 * Checks if all drugs are fully packaged and verified
 */
export const areAllPackagingDrugsVerified = (
  drugVerifications: PackagingDrugVerification[]
): boolean => {
  return drugVerifications.every((v) => isPackagingVerificationComplete(v));
};

/**
 * Gets count of verified drugs
 */
export const getVerifiedPackagingDrugCount = (
  drugVerifications: PackagingDrugVerification[]
): number => {
  return drugVerifications.filter((v) => isPackagingVerificationComplete(v)).length;
};

/**
 * Formats medication name with strength for display
 */
export const formatPackagingMedicationDisplay = (
  medication: PackagingMedicationData
): string => {
  return `${medication.medicationName} ${medication.strength}`.trim();
};

/**
 * Gets overall completion percentage for all drugs
 */
export const getOverallPackagingCompletionPercentage = (
  drugVerifications: PackagingDrugVerification[]
): number => {
  if (drugVerifications.length === 0) return 0;

  const verifiedCount = getVerifiedPackagingDrugCount(drugVerifications);
  return (verifiedCount / drugVerifications.length) * 100;
};

/**
 * Marks a step as complete for a drug
 */
export const markPackagingStepComplete = (
  verification: PackagingDrugVerification,
  stepIndex: number
): PackagingDrugVerification => {
  const completedSteps = [...verification.completedSteps];
  if (!completedSteps.includes(stepIndex)) {
    completedSteps.push(stepIndex);
  }

  return {
    ...verification,
    completedSteps,
  };
};

// =============================================================================
// TABLET QUANTITY HELPERS (T006) - FR-006, FR-007, FR-008
// =============================================================================

/**
 * Initialize tablet quantity state for a medication requiring quantity confirmation
 * @param requiredQuantity - Total quantity needed for the Rx line
 */
export const initializeTabletState = (requiredQuantity: number): TabletQuantityState => {
  return {
    requiredQuantity,
    fulfilledQuantity: 0,
    remainingQuantity: requiredQuantity,
    bottleScans: [],
    isComplete: false,
    vialLabelPrinted: false, // FR-008: Only ONE vial label per Rx line
  };
};

/**
 * Update tablet quantity state when technician enters quantity from a bottle
 * @param state - Current tablet quantity state
 * @param bottleScan - New bottle scan with quantity drawn
 * @returns Updated tablet quantity state with overflow warning if applicable
 */
export const updateTabletQuantity = (
  state: TabletQuantityState,
  bottleScan: TabletBottleScan
): { state: TabletQuantityState; overflowWarning?: string } => {
  const newFulfilled = state.fulfilledQuantity + bottleScan.quantityDrawn;
  const newRemaining = Math.max(0, state.requiredQuantity - newFulfilled);

  // Check for overflow (technician entered more than needed)
  let overflowWarning: string | undefined;
  if (newFulfilled > state.requiredQuantity) {
    overflowWarning = `Warning: Total quantity (${newFulfilled}) exceeds required (${state.requiredQuantity}). Capping at required amount.`;
  }

  // Cap fulfilled quantity at required
  const cappedFulfilled = Math.min(newFulfilled, state.requiredQuantity);

  return {
    state: {
      ...state,
      fulfilledQuantity: cappedFulfilled,
      remainingQuantity: state.requiredQuantity - cappedFulfilled,
      bottleScans: [...state.bottleScans, bottleScan],
      isComplete: cappedFulfilled >= state.requiredQuantity,
    },
    overflowWarning,
  };
};

/**
 * Check if tablet quantity requirement is complete
 * @param state - Current tablet quantity state
 */
export const isTabletComplete = (state: TabletQuantityState | null): boolean => {
  if (!state) return false;
  return state.isComplete;
};

/**
 * Check if additional bottle scan is needed for tablets
 * @param state - Current tablet quantity state
 */
export const needsMoreTablets = (state: TabletQuantityState | null): boolean => {
  if (!state) return false;
  return state.remainingQuantity > 0;
};

// =============================================================================
// CLINIC PATIENT DETECTION (T007) - FR-013
// =============================================================================

/**
 * Detect if a patient is a clinic patient based on order properties
 * Clinic patients have special label printing rules (2 vial labels at end)
 *
 * @param order - Order object with patient/delivery information
 * @returns true if patient is a clinic patient
 */
export const isClinicPatient = (order: {
  isClinicDelivery?: boolean;
  patientType?: string;
  descartesCustomerType?: string;
  metadata?: {
    isClinicDelivery?: boolean;
    patientType?: string;
    descartesCustomerType?: string;
  };
}): boolean => {
  // Check direct properties
  if (order.isClinicDelivery === true) return true;
  if (order.patientType?.toLowerCase() === 'clinic') return true;
  if (order.descartesCustomerType?.toLowerCase()?.includes('clinic')) return true;

  // Check metadata (common pattern in this codebase)
  if (order.metadata?.isClinicDelivery === true) return true;
  if (order.metadata?.patientType?.toLowerCase() === 'clinic') return true;
  if (order.metadata?.descartesCustomerType?.toLowerCase()?.includes('clinic')) return true;

  return false;
};

// =============================================================================
// DOSAGE FORM DETECTION (T008) - FR-006
// =============================================================================

/**
 * List of dosage form strings that indicate tablet medications
 * requiring quantity confirmation
 */
const TABLET_DOSAGE_FORMS = [
  'tablet',
  'tablets',
  'tab',
  'tabs',
  'capsule',
  'capsules',
  'cap',
  'caps',
  'caplet',
  'caplets',
  'pill',
  'pills',
];

/**
 * Extract and normalize dosage form from medication data
 * Returns the dosage form type for workflow branching
 *
 * @param medication - Medication data with drug information
 * @returns Normalized dosage form type
 */
export const extractMedicationDosageForm = (medication: {
  dosageForm?: string;
  form?: string;
  drugName?: string;
  medicationName?: string;
}): DosageForm => {
  // Check explicit dosageForm field first
  const explicitForm = medication.dosageForm?.toLowerCase();
  if (explicitForm) {
    if (TABLET_DOSAGE_FORMS.some(f => explicitForm.includes(f))) {
      return 'tablets';
    }
    if (explicitForm.includes('capsule')) return 'capsules';
    if (explicitForm.includes('liquid') || explicitForm.includes('solution') || explicitForm.includes('syrup')) return 'liquid';
    if (explicitForm.includes('cream')) return 'cream';
    if (explicitForm.includes('ointment')) return 'ointment';
    if (explicitForm.includes('inject')) return 'injection';
    if (explicitForm.includes('inhal')) return 'inhaler';
    if (explicitForm.includes('patch')) return 'patch';
  }

  // Check form field
  const formField = medication.form?.toLowerCase();
  if (formField) {
    if (TABLET_DOSAGE_FORMS.some(f => formField.includes(f))) {
      return 'tablets';
    }
  }

  // Fallback: infer from drug name (common pattern: "Drug Name 50mg Tablets")
  const drugName = (medication.drugName || medication.medicationName || '').toLowerCase();
  if (TABLET_DOSAGE_FORMS.some(f => drugName.includes(f))) {
    return 'tablets';
  }

  return 'other';
};

/**
 * Check if medication requires tablet quantity confirmation
 * @param medication - Medication data with drug information
 */
export const requiresTabletQuantityConfirmation = (medication: {
  dosageForm?: string;
  form?: string;
  drugName?: string;
  medicationName?: string;
}): boolean => {
  const form = extractMedicationDosageForm(medication);
  return form === 'tablets' || form === 'capsules';
};

// =============================================================================
// PACK QUANTITY MATH (GP-384 regression fix)
// =============================================================================

/**
 * Floating-point tolerance for pack-quantity fulfillment comparisons.
 * When quantity / packCount doesn't divide cleanly (e.g. 10 / 3), the sum
 * of scan quantities may fall short by IEEE-754 rounding noise.
 */
export const PACK_QTY_EPSILON = 0.0001;

/**
 * Compute per-pack and total quantities for a medication in the packaging
 * scan flow.
 *
 * IMPORTANT: Do NOT floor these values. `quantity` is the prescribed dose
 * distributed across `packCount` packs, and it may be fractional (e.g. 4.5 mL
 * spread across 3 × 1.5 mL Wegovy dose pens). The invariant is
 * `quantity ≈ packCount × perPackSize`; flooring breaks the invariant and
 * deadlocks the scan step (see GP-384 bug: "0 / 4 units" for a 4.5-unit Rx
 * with 3 packs, unable to auto-complete).
 */
export const computePackQuantities = (med: {
  quantity: number;
  packCount: number;
}): { perPackQty: number; totalQty: number } => {
  const packCount = med.packCount || 1;
  return {
    perPackQty: med.quantity / packCount,
    totalQty: med.quantity,
  };
};

/**
 * GP-1017: default "Qty from this bottle" for the NEXT container in the
 * quantity-driven packaging paths (Split Bottles mode and the compound-mixture
 * "Add Pack" button).
 *
 * GP-384 seeded the first container with the ENTIRE remaining dispense quantity
 * so the technician "only has to edit down". On a drug Kroll says needs several
 * containers that silently ended the fill after the FIRST box: confirming that
 * scan satisfied the quantity gate (`confirmedQty >= totalQty`), which
 * auto-advanced to Additional Labels — where Enter completes packaging — and hid
 * the scanner panel, so the remaining boxes could never be scanned and the
 * delivery label printed early. Reported after a packaging-troubleshoot re-add
 * (Gurjinder, Mississauga, dispensary focus group); prod bears it out — 561
 * multi-box drugs at Mississauga and 269 at London were packaged in the last 30
 * days with a single scan carrying the full prescribed quantity.
 *
 * So: when the dispense is a KNOWN multi-container one (`packCount > 1`) and
 * containers are still outstanding, seed ONE container's share instead of the
 * whole prescription. Single-container drugs (`packCount <= 1`) and the LAST
 * outstanding container keep the full-remainder default, so a genuine
 * one-bottle fill behaves exactly as before — and the field stays editable, so a
 * technician who really drew everything from one bottle just types the total.
 *
 * Fractional shares are preserved (never floored — see computePackQuantities),
 * only trimmed of IEEE-754 noise; the last container takes the exact remainder,
 * so the shares always sum back to `totalQuantity`.
 */
export const defaultContainerScanQuantity = (args: {
  /** Total quantity to dispense for this drug (quantityFilled ?? prescribed). */
  totalQuantity: number;
  /** Quantity already allocated to confirmed containers. */
  usedQuantity: number;
  /** Containers this dispense needs, per Kroll (medication.packCount). */
  packCount: number;
  /** How many containers have already been confirmed. */
  containersUsed: number;
}): number => {
  const remaining = args.totalQuantity - args.usedQuantity;
  // Preserved from GP-384: never seed 0 or a negative — fall back to the total.
  const fullRemainder = remaining > 0 ? remaining : args.totalQuantity;

  const containers = Math.floor(Number(args.packCount)) || 1;
  if (containers <= 1) return fullRemainder;

  const outstanding = containers - Math.max(0, Math.floor(args.containersUsed));
  // Last (or over-run) container absorbs whatever is left, exactly.
  if (outstanding <= 1) return fullRemainder;

  return Math.round((fullRemainder / outstanding) * 1000) / 1000;
};

// =============================================================================
// GP-1018: Vial-label count bounds
// =============================================================================

/**
 * GP-1018: Upper bound for the packaging "Labels to print" stepper (how many
 * copies of a drug's vial label auto-print on completion).
 *
 * This was pinned at 10, which blocked IV/clinic fills that split a stock bottle
 * across many vials — e.g. remdesivir needs ~13 vials, each requiring its own
 * label (Gurjinder, Mississauga dispensary focus group). The cap is raised so
 * those fills can print one label per vial, while keeping a sane ceiling so a
 * stuck "+" (or a bad Kroll Rx.Labels value) can't queue a runaway print job.
 */
export const MAX_VIAL_LABELS = 50;

/**
 * GP-1018: Clamp a requested vial-label count into the printable range.
 * `min` is 0 on the manual stepper (GP-1166: 0 = skip printing) and 1 for the
 * Kroll/packCount-seeded defaults (never default a drug to no label).
 * The upper bound is always {@link MAX_VIAL_LABELS}.
 */
export const clampVialLabelCount = (count: number, min: number = 0): number =>
  Math.max(min, Math.min(MAX_VIAL_LABELS, count));

/**
 * PRX-18: the quantity actually dispensed on this fill's label set — the
 * quantity staff filled (a short fill, or a short balance top-up), else the
 * medication's quantity (on a balance-owing order already the remaining).
 * Every label split is of this quantity, so the shares always sum to what is
 * in the bottles.
 */
export const dispensedQuantity = (
  med: { quantity: number },
  verification?: { quantityFilled?: number } | null,
): number => verification?.quantityFilled ?? med.quantity;

const roundQty = (x: number) => Math.round(x * 1000) / 1000;

/**
 * The "Labels to print" count a drug opens with. PRX-18: the pharmacy system's
 * per-drug split rule first, applied to the dispensed quantity; without one,
 * exactly the earlier order — the Rx's own label count, then one label per
 * pack. Never seeds 0.
 */
export const initialVialLabelCount = (
  med: Pick<PackagingMedicationData, 'labels' | 'packCount' | 'labelSplit'>,
  quantity: number,
): number => {
  const split = labelSplitForQuantity(med.labelSplit, quantity);
  if (split) return Math.min(split.labelCount, maxVialLabelCount(med.labelSplit, quantity));
  if (med.labels != null && med.labels > 0) {
    return clampVialLabelCount(med.labels, 1);
  }
  return clampVialLabelCount(med.packCount || 1, 1);
};

/**
 * PRX-18: even split of `quantity` over `labelCount` labels — the whole-unit
 * floor on every label, leftover whole units one each onto the LAST labels, any
 * fraction on the last label (shares differ by at most one unit). With more
 * labels than whole units (a rule under one unit per label) every label gets
 * an equal fraction instead, so no share is 0. Mirrors the backend's
 * evenLabelShares (common/fillware/label-split.util.ts), which uses it for
 * Fillware's "number of labels" rule.
 */
export const evenLabelShares = (quantity: number, labelCount: number): number[] => {
  const n = Math.floor(labelCount);
  if (!(n >= 1)) return [];
  const base = Math.floor(quantity / n);
  if (base < 1 && n > 1) {
    const each = roundQty(quantity / n);
    return [...Array(n - 1).fill(each), roundQty(quantity - each * (n - 1))];
  }
  const leftover = roundQty(quantity - base * n);
  const whole = Math.floor(leftover);
  const shares = Array.from({ length: n }, (_, i) => base + (i >= n - whole ? 1 : 0));
  shares[n - 1] = roundQty(shares[n - 1] + (leftover - whole));
  return shares;
};

/**
 * PRX-18: the drug's split rule applied to `quantity`, the quantity dispensed
 * on this label set. The stamped split was derived from the resync quantity; a
 * short fill or a short balance top-up dispenses less, and its labels must
 * split what is in the bottles (270 ordered, 180 dispensed, 90 per label: 2
 * labels of 90). Mirrors the backend's deriveFillwareLabelSplit. Null without
 * a rule or when nothing is dispensed.
 */
export const labelSplitForQuantity = (
  split: LabelSplit | null | undefined,
  quantity: number,
): LabelSplit | null => {
  if (!split || !(quantity > 0)) return null;
  const qty = roundQty(quantity);
  if (split.mode === 'qty_per_label') {
    const perLabelQty = Math.min(split.ruleQty, qty);
    // Rounded before the ceil: 4.2 / 1.4 is 3.0000000000000004 in floating
    // point, which would add a 4th label of 0.
    const labelCount = Math.ceil(roundQty(qty / perLabelQty));
    return {
      ...split,
      quantity: qty,
      labelCount,
      perLabelQty: roundQty(perLabelQty),
      lastLabelQty: roundQty(qty - perLabelQty * (labelCount - 1)),
    };
  }
  const labelCount = Math.min(Math.floor(split.ruleQty), Math.max(1, Math.floor(qty)));
  const shares = evenLabelShares(qty, labelCount);
  return { ...split, quantity: qty, labelCount, perLabelQty: shares[0], lastLabelQty: shares[labelCount - 1] };
};

/**
 * PRX-18: the most vial labels staff may pick. Without a rule, the printable
 * maximum (labels are copies of the full quantity). With one, also no more
 * labels than whole units dispensed (or the rule's own count, if a rule under
 * one unit per label asks for more), so no label prints 0.
 */
export const maxVialLabelCount = (split: LabelSplit | null | undefined, quantity: number): number => {
  const rule = labelSplitForQuantity(split, quantity);
  if (!rule) return MAX_VIAL_LABELS;
  return Math.min(MAX_VIAL_LABELS, Math.max(1, Math.floor(quantity), rule.labelCount));
};

/**
 * PRX-18: the quantity each printed vial label carries when the drug has a
 * split rule, label 1 first, for the count that will actually print (the
 * requested count, capped by {@link maxVialLabelCount}). At the rule's own
 * count a "qty per label" rule prints its share on labels 1..N-1 and the
 * remainder on label N (100 at 90: 90, 10); a "number of labels" rule splits
 * evenly. Any other count is an even split, so a split never prints the full
 * quantity on several labels. The shares always sum to `quantity`, the
 * dispensed quantity. Null without a rule (every label shows the full
 * quantity, as before), with nothing dispensed, or with no labels.
 */
export const vialLabelShares = (
  split: LabelSplit | null | undefined,
  labelCount: number,
  quantity: number,
): number[] | null => {
  const rule = labelSplitForQuantity(split, quantity);
  if (!rule || !(labelCount >= 1)) return null;
  const n = Math.min(Math.floor(labelCount), maxVialLabelCount(split, quantity));
  if (n === rule.labelCount && rule.mode === 'qty_per_label') {
    return [...Array(n - 1).fill(rule.perLabelQty), rule.lastLabelQty];
  }
  return evenLabelShares(rule.quantity!, n);
};

/** PRX-18: one printed vial label's place in a split fill, and its share. */
export interface SplitLabel {
  index: number;
  count: number;
  quantity: number;
}

/**
 * PRX-18: the split labels to print, label 1 first — the one helper every vial
 * label print path uses (automatic print, F1, and both manual print buttons).
 * Its length is the number of labels that print. Null when the drug has no
 * split (print `labelCount` copies of the full quantity, as before).
 */
export const splitLabelsForPrint = (
  split: LabelSplit | null | undefined,
  labelCount: number,
  quantity: number,
): SplitLabel[] | null => {
  const shares = vialLabelShares(split, labelCount, quantity);
  return shares ? shares.map((q, i) => ({ index: i + 1, count: shares.length, quantity: q })) : null;
};

/** "3 labels of 90 (last label 20)", or "4 labels: 2, 2, 3, 3" when uneven. */
const describeShares = (shares: number[]): string => {
  const n = shares.length;
  const noun = n === 1 ? 'label' : 'labels';
  const head = shares.slice(0, -1);
  if (head.every((q) => q === shares[0])) {
    const last = n > 1 && shares[n - 1] !== shares[0] ? ` (last label ${shares[n - 1]})` : '';
    return `${n} ${noun} of ${shares[0]}${last}`;
  }
  return `${n} ${noun}: ${shares.join(', ')}`;
};

/** The rule's own split, before any cap: per/last for qty per label, else even. */
const ruleShares = (rule: LabelSplit): number[] =>
  rule.mode === 'qty_per_label'
    ? [...Array(rule.labelCount - 1).fill(rule.perLabelQty), rule.lastLabelQty]
    : evenLabelShares(rule.quantity ?? rule.perLabelQty * (rule.labelCount - 1) + rule.lastLabelQty, rule.labelCount);

/** PRX-18: "Split by rule: 3 labels of 90 (last label 20)". */
export const formatLabelSplitNote = (split: LabelSplit): string =>
  `Split by rule: ${describeShares(ruleShares(split))}`;

/**
 * PRX-18: the note under "Labels to print", for the dispensed quantity. With a
 * rule: the rule's split; when the rule needs more labels than can print, the
 * cap and what prints instead; when staff changed the count, the shares that
 * will actually print; at the whole-unit cap, why the count stops there.
 * Without a rule, more than one label means copies of the full quantity
 * (unchanged behaviour, said plainly). Null when there is nothing to say.
 */
export const formatVialLabelCountNote = (
  split: LabelSplit | null | undefined,
  labelCount: number,
  quantity: number,
): string | null => {
  if (!split) return labelCount > 1 ? `${labelCount} copies, each shows the full quantity` : null;
  const rule = labelSplitForQuantity(split, quantity);
  if (!rule) return null;
  const max = maxVialLabelCount(split, quantity);
  const parts = [formatLabelSplitNote(rule)];
  if (rule.labelCount > MAX_VIAL_LABELS) {
    const capped = vialLabelShares(split, MAX_VIAL_LABELS, quantity) ?? [];
    parts.push(`Label count capped at ${MAX_VIAL_LABELS}: ${describeShares(capped)}`);
  }
  if (buildLabelSplitOverride(split, labelCount, quantity)) {
    const shares = vialLabelShares(split, labelCount, quantity);
    parts.push(
      shares
        ? `Changed by you to ${describeShares(shares)}; recorded in the order history`
        : 'Changed by you; recorded in the order history',
    );
  }
  if (labelCount >= max && max < MAX_VIAL_LABELS) {
    parts.push(`At most ${max} labels, so that no label prints 0`);
  }
  const note = parts.join('. ');
  return parts.length > 1 ? `${note}.` : note;
};

/**
 * PRX-18: the override the packaging item carries when staff print a different
 * number of labels than the rule gives for the dispensed quantity (0
 * included). Compared with the count the stepper opens at, so a rule above the
 * printable maximum is not reported as a staff change.
 */
export const buildLabelSplitOverride = (
  split: LabelSplit | null | undefined,
  labelsPrinted: number,
  quantity: number,
): LabelSplitOverride | undefined => {
  const rule = labelSplitForQuantity(split, quantity);
  if (!rule) return undefined;
  const ruleLabelCount = Math.min(rule.labelCount, maxVialLabelCount(split, quantity));
  if (labelsPrinted === ruleLabelCount) return undefined;
  return { mode: rule.mode, ruleQty: rule.ruleQty, ruleLabelCount, labelsPrinted };
};

// =============================================================================
// GP-665: Balance-owing vial label fields
// =============================================================================

/** GP-665: vial-label fields for partial / balance fills ("X of Y" + indicator). */
export interface BalanceLabelFields {
  amountSent?: number;
  prescribedQty?: number;
  balanceIndicator?: 'owing' | 'balance';
}

/**
 * GP-665: derive the balance fields to merge into a vial label's print data.
 *
 * - Balance-owing (top-up) order → 'balance': X = qty being sent this fill,
 *   Y = originally prescribed qty from `metadata.balanceOwing.originalQuantities`.
 *   Without that metadata entry the label stays normal — we never guess the
 *   original prescribed quantity.
 * - Initial partial fill (technician lowered the quantity below prescribed)
 *   → 'owing': X = quantityFilled, Y = prescribed.
 * - Full fill (or zero fill, which prints no labels at all) → {} — the label
 *   renders exactly as today.
 */
export const getBalanceLabelFields = (
  order: { isBalanceOwing?: boolean; metadata?: any } | null | undefined,
  med: { rxNumber: string; quantity: number },
  verification?: { quantityFilled?: number } | null,
): BalanceLabelFields => {
  if (order?.isBalanceOwing) {
    // String-coerce the rxNumber comparison — metadata may store it as a number
    // (same defence as elsewhere in this file and OrdersService.resyncOrderWithKroll).
    const boInfo = order.metadata?.balanceOwing?.originalQuantities?.find(
      (oq: any) => String(oq.rxNumber) === String(med.rxNumber),
    );
    const originalQty = boInfo?.originalQuantity != null ? Number(boInfo.originalQuantity) : NaN;
    if (Number.isFinite(originalQty) && originalQty > 0) {
      // med.quantity on a BO order is already corrected to the remaining amount;
      // quantityFilled reflects a further short fill of the top-up itself.
      const sendingNow = verification?.quantityFilled ?? med.quantity;
      if (sendingNow > 0) {
        return {
          amountSent: sendingNow,
          prescribedQty: originalQty,
          balanceIndicator: 'balance',
        };
      }
    }
    return {};
  }

  const filled = verification?.quantityFilled;
  if (filled != null && filled > 0 && filled < med.quantity) {
    return { amountSent: filled, prescribedQty: med.quantity, balanceIndicator: 'owing' };
  }
  return {};
};

/* ------------------------------------------------------------------------
 * Lot / expiry helpers (GP-1007)
 *
 * Moved here from PackagingVerification.tsx so the ordering guarantee below
 * is unit-testable without mounting the dialog. These decide which expiry
 * dates reach a printed vial label, so they need direct test cover.
 * ---------------------------------------------------------------------- */

// Sort key for expiry strings in MM/YYYY or DD/MM/YYYY format. Produces a number
// YYYYMMDD so lexicographic/numeric comparison gives correct chronological order.
// Missing/invalid expiries sort LAST so they never "win" the earliest pick.
export const expirySortKey = (expiry: string | undefined | null): number => {
  if (!expiry) return Number.POSITIVE_INFINITY;
  const parts = expiry.split('/');
  let day = 1;
  let month: number;
  let year: number;
  if (parts.length === 2) {
    // MM/YYYY — treat as end of month so MM/YYYY scans don't beat a DD/MM/YYYY
    // scan that's actually earlier in the same month.
    month = parseInt(parts[0], 10);
    year = parseInt(parts[1], 10);
    day = 31;
  } else if (parts.length === 3) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else {
    return Number.POSITIVE_INFINITY;
  }
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return Number.POSITIVE_INFINITY;
  }
  return year * 10000 + month * 100 + day;
};

// Pick the scan whose expiry comes soonest. Ties fall back to the first scan in
// input order. Used to produce a single consolidated vial label for a drug that
// was packaged from multiple lots — label shows the lot/expiry most at risk.
export const pickEarliestExpiryScan = <T extends { lotNumber?: string; expiryDate?: string }>(scans: T[]): T => {
  return scans.reduce((best, s) => (expirySortKey(s.expiryDate) < expirySortKey(best.expiryDate) ? s : best), scans[0]);
};

// GP-1007: collect the DISTINCT lot/expiry pairs across the confirmed packs so a
// vial label that covers multiple packs lists EVERY lot/expiry instead of only
// the first/earliest one (mixed packs with different expiries, split bottles,
// even sealed packs entered per pack). Returns undefined for the ordinary
// single-lot case so those labels render exactly as before (no regression).
export const collectLotExpiryPairs = (
  scans: Array<{ lotNumber?: string; expiryDate?: string; confirmed?: boolean }>,
): Array<{ lot: string; expiry: string }> | undefined => {
  const seen = new Set<string>();
  const pairs: Array<{ lot: string; expiry: string }> = [];
  for (const s of scans) {
    if (s.confirmed === false) continue;
    const lot = (s.lotNumber || '').trim();
    const expiry = (s.expiryDate || '').trim();
    if (!lot && !expiry) continue;
    const key = `${lot}|${expiry}`;
    if (seen.has(key)) continue;
    seen.add(key);
    pairs.push({ lot, expiry });
  }
  // Order by expiry, soonest first. The label renderer caps how many expiry
  // lines fit above the barcode (MAX_EXPIRY_LINES), so whatever ordering this
  // returns decides which expiries survive that cap. Scan order would let a
  // packager who happened to scan the shortest-dated pack last push it off the
  // label — leaving a vial whose printed dates all run LATER than the drug it
  // actually contains. Sorting ascending makes the cap drop only the longest
  // dates, so the earliest expiry (the one that governs the beyond-use date) is
  // always printed. expirySortKey sorts missing/invalid expiries last.
  // Compare, don't subtract: two missing expiries are both POSITIVE_INFINITY and
  // Infinity - Infinity is NaN, which is not a valid comparator result.
  pairs.sort((a, b) => {
    const ka = expirySortKey(a.expiry);
    const kb = expirySortKey(b.expiry);
    return ka === kb ? 0 : ka < kb ? -1 : 1;
  });
  return pairs.length > 1 ? pairs : undefined;
};

/** The lot/expiry fields a reprint must reproduce for one Rx (GP-1008). */
export interface ReprintLotExpiry {
  /** Primary lot — the earliest-expiring pack, as the original label printed. */
  lotNumber?: string;
  /** Primary expiry, same string the original print sent to /api/printing/label-data. */
  expiryDate?: string;
  /** GP-1007 multi-lot list, present only when the drug came from >1 lot/expiry. */
  lotNumbers?: Array<{ lot: string; expiry: string }>;
}

/** GP-1007: the lot/expiry source for ONE vial label about to be printed. */
export interface VialLabelPrintPlanEntry<T> {
  /** The pack whose lot/expiry this label carries. */
  scan: T;
  /**
   * Every distinct lot/expiry this ONE label stands for. Set only when the label
   * covers MORE than one pack; undefined for a per-pack label (it carries its
   * own pack's values) and for a single-lot fill.
   */
  lotNumbers?: Array<{ lot: string; expiry: string }>;
}

/**
 * GP-1008: recover the lot/expiry a given Rx was PACKAGED with, so a reprint of
 * its vial label reproduces the label stuck on the product.
 *
 * Kroll holds no lot/expiry — `/api/printing/label-data` echoes back whatever the
 * caller passes — so a reprint that can't supply these prints a blank expiry and
 * the second label no longer matches the first. The durable record is
 * `order.metadata.packagingData`, which packaging merges BY RX NUMBER, so it is an
 * ARRAY of per-Rx records (a pre-merge order may still hold the legacy single
 * object; both shapes are read here, exactly as HardcopyViewer does).
 *
 * The values are rebuilt through the same helpers the original print used
 * (`pickEarliestExpiryScan` for the primary lot/expiry, `collectLotExpiryPairs`
 * for the multi-lot list), so the reprint reproduces the original's content and
 * ordering rather than an independently-derived approximation.
 *
 * Returns undefined when this Rx has no packaging record — never another Rx's
 * lot/expiry. A record without an rxNumber (legacy) is only used when it is the
 * order's ONLY record; borrowing it for a sibling drug would print an expiry that
 * is not in that vial.
 */
export const getPackagedLotExpiryForRx = (
  packagingData: unknown,
  rxNumber: string | number | undefined | null,
): ReprintLotExpiry | undefined => {
  if (!packagingData) return undefined;
  const items = (Array.isArray(packagingData) ? packagingData : [packagingData]).filter(
    Boolean,
  ) as Array<Record<string, any>>;
  if (items.length === 0) return undefined;

  const rx = String(rxNumber ?? '').trim();
  const record =
    (rx ? items.find((i) => String(i.rxNumber ?? '').trim() === rx) : undefined) ??
    (items.length === 1 && !items[0].rxNumber ? items[0] : undefined);
  if (!record) return undefined;

  // Scans carry the lot/expiry AS ENTERED at packaging — the exact strings the
  // original print sent. The item-level pair below is a normalized copy of the
  // last scan and is only the fallback for records saved without scans.
  const scans = (Array.isArray(record.scans) ? record.scans : []).filter(
    (s: any) =>
      s && s.confirmed !== false && (String(s.lotNumber ?? '').trim() || String(s.expiryDate ?? '').trim()),
  ) as Array<{ lotNumber?: string; expiryDate?: string; confirmed?: boolean }>;

  if (scans.length > 0) {
    const primary = pickEarliestExpiryScan(scans);
    const lotNumbers = collectLotExpiryPairs(scans);
    return {
      lotNumber: String(primary?.lotNumber ?? '').trim() || undefined,
      expiryDate: String(primary?.expiryDate ?? '').trim() || undefined,
      ...(lotNumbers ? { lotNumbers } : {}),
    };
  }

  const lotNumber = String(record.lotNumber ?? '').trim() || undefined;
  const expiryDate = String(record.expiryDate ?? '').trim() || undefined;
  if (!lotNumber && !expiryDate) return undefined;
  return { lotNumber, expiryDate };
};

/**
 * GP-1007: decide which lot/expiry each printed vial label carries.
 *
 * A drug can be packaged from several packs, each with its OWN lot and expiry
 * (mixed packs, split bottles, even sealed packs entered per pack). Every label
 * used to be printed from a single scan, so the whole run went out stamped with
 * one pack's values — the compliance gap Denise/Gurjinder raised (group homes /
 * Ontario / PRNs read the expiry off the vial).
 *
 * - **One label per pack** (`labelCount === confirmedScans.length`, the default
 *   — the "Labels to print" count is seeded from `packCount`, "each box gets a
 *   vial label"): label *i* carries pack *i*'s own lot/expiry, in pack order, so
 *   each vial is labelled with what is actually inside it.
 * - **Otherwise** one label covers several packs (staff changed the stepper, or
 *   Kroll's `Rx.Labels` differs from the pack count), so every copy lists EVERY
 *   distinct lot/expiry via {@link collectLotExpiryPairs} — nothing is dropped.
 *   The earliest-expiring pack still supplies the single lot/expiry fields.
 *
 * A single-lot fill takes either branch to the same output, so ordinary labels
 * are unchanged.
 */
export const buildVialLabelPrintPlan = <
  T extends { lotNumber?: string; expiryDate?: string; confirmed?: boolean },
>(
  confirmedScans: T[],
  labelCount: number,
): Array<VialLabelPrintPlanEntry<T>> => {
  if (!(labelCount > 0) || confirmedScans.length === 0) return [];

  if (labelCount === confirmedScans.length) {
    return confirmedScans.map((scan) => ({ scan }));
  }

  const lotNumbers = collectLotExpiryPairs(confirmedScans);
  const consolidatedScan = pickEarliestExpiryScan(confirmedScans);
  return Array.from({ length: Math.floor(labelCount) }, () => ({
    scan: consolidatedScan,
    lotNumbers,
  }));
};

/**
 * PRX-18: the vial labels the automatic print on drug completion (and F1)
 * sends, in order: {@link buildVialLabelPrintPlan}'s lot/expiry per label, plus
 * each label's share of a split fill from {@link splitLabelsForPrint}. With a
 * split, the number of labels is the split's (the requested count, capped so
 * no label prints 0); without one, `labelCount` copies as before.
 */
export const planVialLabelPrintJobs = <
  T extends { lotNumber?: string; expiryDate?: string; confirmed?: boolean },
>(
  confirmedScans: T[],
  labelCount: number,
  split: LabelSplit | null | undefined,
  quantity: number,
): Array<VialLabelPrintPlanEntry<T> & { splitLabel?: SplitLabel }> => {
  if (!(labelCount > 0)) return [];
  const splitLabels = splitLabelsForPrint(split, labelCount, quantity);
  const plan = buildVialLabelPrintPlan(confirmedScans, splitLabels?.length ?? labelCount);
  return plan.map((entry, i) => (splitLabels ? { ...entry, splitLabel: splitLabels[i] } : entry));
};
